import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UtilsService } from '../utils.service';

interface PhoneInput {
  control: FormControl;

  maskPattern: string;

  regexPattern: RegExp;

  charactersCount: number;

  defaultValue: string;
}

@Component({
  selector: 'lp2-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
})
export class PhoneInputComponent implements OnChanges {
  @Input()
  pattern: string;

  @Input()
  form: FormGroup;

  @Input()
  controlName: string;

  @Input()
  extraPrefix = '';

  @Input()
  fieldsRequired = false;

  @ViewChildren('inputs')
  inputs: QueryList<ElementRef>;

  inputControls: PhoneInput[] = [];

  currentPattern: string;

  ngOnChanges(changes: SimpleChanges) {
    if (this.pattern && this.form && this.controlName) {
      this.initialize();
    }
  }

  initialize() {
    if (this.currentPattern !== this.pattern) {
      this.currentPattern = this.pattern;
      this.inputControls = [];
      let formValue = this.form.get(this.controlName).value.userEnteredData;

      // Calculate the number of characters and the default value
      for (const pat of this.pattern.split('-')) {
        let charactersCount = 0;
        let isInOpener = false;
        let defaultValue = '';
        let openerCount = 0;
        let maxOpenerCount = 0;

        for (const letter of pat) {
          switch (letter) {
            case '(':
            case ')':
            // Country code wrapper
            case '{':
            case '}':
              // State code wrapper
              break;
            case '[':
              isInOpener = true;
              openerCount = 0;
              break;
            case ']':
              isInOpener = false;
              charactersCount += maxOpenerCount;
              break;
            default:
              if (isInOpener) {
                if (letter !== ',') {
                  openerCount++;
                } else {
                  if (maxOpenerCount < openerCount) {
                    maxOpenerCount = openerCount;
                  }
                  openerCount = 0;
                }
              } else {
                charactersCount++;
                if (letter !== 'X' && letter !== ',') {
                  defaultValue += letter;
                }
              }
          }
        }

        // Check if it is readonly input or not
        const regexPattern = UtilsService.convertMaskToRegEx(pat);
        if (defaultValue.length !== charactersCount) {
          defaultValue = '';
        }

        // Preload user value
        let userValue = '';
        if (!defaultValue) {
          userValue += formValue.substr(0, charactersCount);
          formValue = formValue.substr(charactersCount);
        }

        const validators = [Validators.pattern(regexPattern)];
        if (this.fieldsRequired) {
          validators.push(Validators.required);
        }

        this.inputControls.push({
          control: new FormControl(userValue || defaultValue, validators),
          maskPattern: pat,
          charactersCount,
          regexPattern,
          defaultValue,
        });
      }

      this.inputChanged();
    }
  }

  inputChanged(input: PhoneInput = undefined) {
    let userValue = '';
    let staticValue = '';

    for (const ctr of this.inputControls) {
      if (ctr.defaultValue) {
        staticValue += ctr.control.value;
      } else {
        userValue += ctr.control.value;
      }
    }

    this.form.get(this.controlName).setValue({
      userEnteredData: userValue,
      data: `${this.extraPrefix}${staticValue}${userValue}`,
    });

    if (input) {
      this.autoFocusNext(input);
    }
  }

  autoFocusPrev(input: PhoneInput) {
    const index = this.inputControls.indexOf(input);
    if (input.control.value.length < 1 && index > 0) {
      this.focusControl(index - 1);
    }
  }

  autoFocusNext(input: PhoneInput) {
    const index = this.inputControls.indexOf(input);
    if (
      input.control.value.length === input.charactersCount &&
      index + 1 < this.inputControls.length
    ) {
      this.focusControl(index + 1);
    }
  }

  focusControl(index: number) {
    const control = this.inputControls[index];
    if (!control.defaultValue) {
      this.inputs.toArray()[index].nativeElement.focus();
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/phone-input/phone-input.component.ts